import { DataService } from './data-service';

// Filter Interfaces
export interface StatsFilters {
  startDate?: string;
  endDate?: string;
  propertyId?: string;
  projectId?: string;
}

export interface PaymentFilters {
  days?: number;
  includeOverdue?: boolean;
  propertyId?: string;
  projectId?: string;
  tenantId?: string;
  status?: string;
}

export interface RevenueFilters {
  startDate?: string;
  endDate?: string;
  feeTypeId?: string;
  currencyId?: string;
  propertyId?: string;
  projectId?: string;
}

export interface UnitAnalyticsFilters {
  limit?: number;
  startDate?: string;
  endDate?: string;
  propertyId?: string;
  projectId?: string;
}

export interface ExpiringLeasesFilters {
  days?: number;
  propertyId?: string;
  projectId?: string;
}

export interface NewInstallmentsFilters {
  pageNumber?: number;
  pageSize?: number;
  propertyId?: string;
  projectId?: string;
}

// TypeScript Interfaces for Dashboard Data
export interface DashboardStats {
  totalActiveTenants: number;
  totalUsers: number;
  totalActiveLeases: number;
  totalUnits: number;
  occupiedUnits: number;
  vacantUnits: number;
  totalProjects: number;
  totalProperties: number;
  totalRevenue: number;
  outstandingPayments: number;
  overduePaymentsCount: number;
  paymentsDueThisWeek: number;
  leasesExpiringWithin2Months: number;
}

export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone: string;
  active: boolean;
}

export interface Unit {
  id: string;
  number: string;
  propertyId: string;
  propertyCode: string;
}

export interface PaymentDue {
  installmentId: string;
  leaseId: string;
  leaseRefNumber: string;
  tenant: Tenant;
  unit: Unit;
  amount: number;
  dueDate: string;
  daysUntilDue: number;
  status: string;
  feeTypeName: string;
  currencyCode: string;
}

export interface RevenueByType {
  feeTypeId: string;
  feeTypeName: string;
  totalAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export interface RevenueStats {
  totalRevenue: number;
  totalCollected: number;
  totalOutstanding: number;
  revenueThisMonth: number;
  revenueThisYear: number;
  revenueByType: RevenueByType[];
}

export interface UnitPerformance {
  unitId: string;
  unitNumber: string;
  propertyCode: string;
  projectName: string;
  totalLeases: number;
  activeLeases: number;
  totalRevenue: number;
}

export interface UnitAnalytics {
  mostRentedUnits: UnitPerformance[];
  topRevenueUnits: UnitPerformance[];
  averageLeaseDurationMonths: number;
  averageOccupancyRate: number;
}

export interface ExpiringLease {
  id: string;
  refNumber: string;
  startDate: string;
  endDate: string;
  status: string;
  leaseType: string;
  currency: string;
  unit: {
    id: string;
    number: string;
    rooms: number;
    bathrooms: number;
    floor: number;
    areaM2: number;
    propertyId: string;
  };
  tenant: {
    id: string;
    fullName: string;
    phoneNumbers: string[];
    emails: string[];
    nationality: string;
  };
  fees: Array<{
    id: string;
    value: number;
    type: string;
    currency: string;
    billingCycle: string;
  }>;
}

export interface NewInstallment {
  id: string;
  issuedAt: string;
  dueDate: string;
  status: string;
  total: number;
  currency: string;
  fee: {
    id: string;
    value: number;
    feeTypeId: string;
    type: string;
    currencyId: string;
    penaltyPercentage: number;
    currency: string;
    billingCycle: string;
    notes: string;
    active: boolean;
  };
  lease: {
    id: string;
    createdDate: string;
    startDate: string;
    endDate: string;
    checkinDate: string;
    checkoutDate: string;
    status: string;
    refNumber: string;
    leaseTypeId: string;
    currencyId: string;
    active: boolean;
  };
  active: boolean;
}

export interface NewInstallmentsResponse {
  items: NewInstallment[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export class DashboardService {
  private static readonly BASE_PATH = 'api/dashboard';

  static async getStats(filters?: StatsFilters): Promise<DashboardStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);

      const queryString = params.toString();
      const url = queryString
        ? `${this.BASE_PATH}/stats?${queryString}`
        : `${this.BASE_PATH}/stats`;

      const response = await DataService.get(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard stats: ${response.statusText}`);
      }

      const result = await response.json();

      const data = result.data || result;

      return this.validateDashboardStats(data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  static async getPaymentsDue(filters?: PaymentFilters): Promise<PaymentDue[]> {
    try {
      const params = new URLSearchParams();
      params.append('days', (filters?.days ?? 30).toString());
      params.append('includeOverdue', (filters?.includeOverdue ?? true).toString());

      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);
      if (filters?.tenantId) params.append('tenantId', filters.tenantId);
      if (filters?.status) params.append('status', filters.status);

      const response = await DataService.get(
        `${this.BASE_PATH}/payments-due?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch payments due: ${response.statusText}`);
      }

      const result = await response.json();

      const data = result.data || result;

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: expected array');
      }

      return data.map(payment => this.sanitizePaymentDue(payment));
    } catch (error) {
      console.error('Error fetching payments due:', error);
      throw error;
    }
  }

  static async getRevenueStats(filters?: RevenueFilters): Promise<RevenueStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.feeTypeId) params.append('feeTypeId', filters.feeTypeId);
      if (filters?.currencyId) params.append('currencyId', filters.currencyId);
      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);

      const queryString = params.toString();
      const url = queryString
        ? `${this.BASE_PATH}/revenue?${queryString}`
        : `${this.BASE_PATH}/revenue`;

      const response = await DataService.get(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch revenue stats: ${response.statusText}`);
      }

      const result = await response.json();

      const data = result.data || result;

      return this.validateRevenueStats(data);
    } catch (error) {
      console.error('Error fetching revenue stats:', error);
      throw error;
    }
  }

  static async getUnitAnalytics(filters?: UnitAnalyticsFilters): Promise<UnitAnalytics> {
    try {
      const params = new URLSearchParams();
      params.append('limit', (filters?.limit ?? 10).toString());

      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);

      const response = await DataService.get(
        `${this.BASE_PATH}/unit-analytics?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch unit analytics: ${response.statusText}`);
      }

      const result = await response.json();

      const data = result.data || result;

      return this.validateUnitAnalytics(data);
    } catch (error) {
      console.error('Error fetching unit analytics:', error);
      throw error;
    }
  }

  static async getExpiringLeases(filters?: ExpiringLeasesFilters): Promise<ExpiringLease[]> {
    try {
      const params = new URLSearchParams();
      params.append('days', (filters?.days ?? 30).toString());

      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);

      const response = await DataService.get(
        `api/Lease/expiring?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch expiring leases: ${response.statusText}`);
      }

      const result = await response.json();

      const data = result.data || result;

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: expected array');
      }

      return data.map(lease => this.sanitizeExpiringLease(lease));
    } catch (error) {
      console.error('Error fetching expiring leases:', error);
      throw error;
    }
  }

  static async getNewInstallments(filters?: NewInstallmentsFilters): Promise<NewInstallmentsResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.propertyId) params.append('propertyId', filters.propertyId);
      if (filters?.projectId) params.append('projectId', filters.projectId);

      const queryString = params.toString();
      const url = queryString ? `api/Installment/new?${queryString}` : `api/Installment/new`;

      const response = await DataService.get(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch new installments: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || result;

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: expected array');
      }

      const allItems = data.map(installment => this.sanitizeNewInstallment(installment));
      const pageNumber = filters?.pageNumber ?? 1;
      const pageSize = filters?.pageSize ?? 10;
      const totalCount = allItems.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const items = allItems.slice(startIndex, endIndex);

      return {
        items,
        totalCount,
        pageNumber,
        pageSize,
        totalPages
      };
    } catch (error) {
      console.error('Error fetching new installments:', error);
      throw error;
    }
  }


  private static validateDashboardStats(data: any): DashboardStats {
    return {
      totalActiveTenants: this.sanitizeNumber(data.totalActiveTenants),
      totalUsers: this.sanitizeNumber(data.totalUsers),
      totalActiveLeases: this.sanitizeNumber(data.totalActiveLeases),
      totalUnits: this.sanitizeNumber(data.totalUnits),
      occupiedUnits: this.sanitizeNumber(data.occupiedUnits),
      vacantUnits: this.sanitizeNumber(data.vacantUnits),
      totalProjects: this.sanitizeNumber(data.totalProjects),
      totalProperties: this.sanitizeNumber(data.totalProperties),
      totalRevenue: this.sanitizeNumber(data.totalRevenue),
      outstandingPayments: this.sanitizeNumber(data.outstandingPayments),
      overduePaymentsCount: this.sanitizeNumber(data.overduePaymentsCount),
      paymentsDueThisWeek: this.sanitizeNumber(data.paymentsDueThisWeek),
      leasesExpiringWithin2Months: this.sanitizeNumber(data.leasesExpiringWithin2Months)
    };
  }

  private static sanitizePaymentDue(payment: any): PaymentDue {
    return {
      installmentId: this.sanitizeString(payment.installmentId),
      leaseId: this.sanitizeString(payment.leaseId),
      leaseRefNumber: this.sanitizeString(payment.leaseRefNumber),
      tenant: {
        id: this.sanitizeString(payment.tenant?.id),
        name: this.sanitizeString(payment.tenant?.fullName || payment.tenant?.name),
        email: this.sanitizeString(
          Array.isArray(payment.tenant?.emails)
            ? payment.tenant.emails[0]
            : payment.tenant?.email
        ),
        phone: this.sanitizeString(
          Array.isArray(payment.tenant?.phoneNumbers)
            ? payment.tenant.phoneNumbers[0]
            : payment.tenant?.phone
        ),
        active: Boolean(payment.tenant?.active)
      },
      unit: {
        id: this.sanitizeString(payment.unit?.id),
        number: this.sanitizeString(payment.unit?.number),
        propertyId: this.sanitizeString(payment.unit?.propertyId),
        propertyCode: this.sanitizeString(
          payment.unit?.property?.code || payment.unit?.propertyCode
        )
      },
      amount: this.sanitizeNumber(payment.amount),
      dueDate: this.sanitizeString(payment.dueDate),
      daysUntilDue: this.sanitizeNumber(payment.daysUntilDue),
      status: this.sanitizeString(payment.status),
      feeTypeName: this.sanitizeString(payment.feeTypeName),
      currencyCode: this.sanitizeString(payment.currencyCode)
    };
  }

  private static validateRevenueStats(data: any): RevenueStats {
    return {
      totalRevenue: this.sanitizeNumber(data.totalRevenue),
      totalCollected: this.sanitizeNumber(data.totalCollected),
      totalOutstanding: this.sanitizeNumber(data.totalOutstanding),
      revenueThisMonth: this.sanitizeNumber(data.revenueThisMonth),
      revenueThisYear: this.sanitizeNumber(data.revenueThisYear),
      revenueByType: Array.isArray(data.revenueByType)
        ? data.revenueByType.map((item: any) => ({
            feeTypeId: this.sanitizeString(item.feeTypeId),
            feeTypeName: this.sanitizeString(item.feeTypeName),
            totalAmount: this.sanitizeNumber(item.totalAmount),
            collectedAmount: this.sanitizeNumber(item.collectedAmount),
            outstandingAmount: this.sanitizeNumber(item.outstandingAmount)
          }))
        : []
    };
  }

  private static validateUnitAnalytics(data: any): UnitAnalytics {
    const sanitizeUnit = (unit: any): UnitPerformance => ({
      unitId: this.sanitizeString(unit.unitId),
      unitNumber: this.sanitizeString(unit.unitNumber),
      propertyCode: this.sanitizeString(unit.propertyCode),
      projectName: this.sanitizeString(unit.projectName),
      totalLeases: this.sanitizeNumber(unit.totalLeases),
      activeLeases: this.sanitizeNumber(unit.activeLeases),
      totalRevenue: this.sanitizeNumber(unit.totalRevenue)
    });

    return {
      mostRentedUnits: Array.isArray(data.mostRentedUnits)
        ? data.mostRentedUnits.map(sanitizeUnit)
        : [],
      topRevenueUnits: Array.isArray(data.topRevenueUnits)
        ? data.topRevenueUnits.map(sanitizeUnit)
        : [],
      averageLeaseDurationMonths: this.sanitizeNumber(data.averageLeaseDurationMonths),
      averageOccupancyRate: this.sanitizeNumber(data.averageOccupancyRate)
    };
  }

  private static sanitizeExpiringLease(lease: any): ExpiringLease {
    return {
      id: this.sanitizeString(lease.id),
      refNumber: this.sanitizeString(lease.refNumber),
      startDate: this.sanitizeString(lease.startDate),
      endDate: this.sanitizeString(lease.endDate),
      status: this.sanitizeString(lease.status),
      leaseType: this.sanitizeString(lease.leaseType),
      currency: this.sanitizeString(lease.currency),
      unit: {
        id: this.sanitizeString(lease.unit?.id),
        number: this.sanitizeString(lease.unit?.number),
        rooms: this.sanitizeNumber(lease.unit?.rooms),
        bathrooms: this.sanitizeNumber(lease.unit?.bathrooms),
        floor: this.sanitizeNumber(lease.unit?.floor),
        areaM2: this.sanitizeNumber(lease.unit?.areaM2),
        propertyId: this.sanitizeString(lease.unit?.propertyId)
      },
      tenant: {
        id: this.sanitizeString(lease.tenant?.id),
        fullName: this.sanitizeString(lease.tenant?.fullName),
        phoneNumbers: Array.isArray(lease.tenant?.phoneNumbers)
          ? lease.tenant.phoneNumbers
          : [],
        emails: Array.isArray(lease.tenant?.emails)
          ? lease.tenant.emails
          : [],
        nationality: this.sanitizeString(lease.tenant?.nationality)
      },
      fees: Array.isArray(lease.fees)
        ? lease.fees.map((fee: any) => ({
            id: this.sanitizeString(fee.id),
            value: this.sanitizeNumber(fee.value),
            type: this.sanitizeString(fee.type),
            currency: this.sanitizeString(fee.currency),
            billingCycle: this.sanitizeString(fee.billingCycle)
          }))
        : []
    };
  }

  private static sanitizeNewInstallment(installment: any): NewInstallment {
    return {
      id: this.sanitizeString(installment.id),
      issuedAt: this.sanitizeString(installment.issuedAt),
      dueDate: this.sanitizeString(installment.dueDate),
      status: this.sanitizeString(installment.status),
      total: this.sanitizeNumber(installment.total),
      currency: this.sanitizeString(installment.currency),
      fee: {
        id: this.sanitizeString(installment.fee?.id),
        value: this.sanitizeNumber(installment.fee?.value),
        feeTypeId: this.sanitizeString(installment.fee?.feeTypeId),
        type: this.sanitizeString(installment.fee?.type),
        currencyId: this.sanitizeString(installment.fee?.currencyId),
        penaltyPercentage: this.sanitizeNumber(installment.fee?.penaltyPercentage),
        currency: this.sanitizeString(installment.fee?.currency),
        billingCycle: this.sanitizeString(installment.fee?.billingCycle),
        notes: this.sanitizeString(installment.fee?.notes),
        active: Boolean(installment.fee?.active)
      },
      lease: {
        id: this.sanitizeString(installment.lease?.id),
        createdDate: this.sanitizeString(installment.lease?.createdDate),
        startDate: this.sanitizeString(installment.lease?.startDate),
        endDate: this.sanitizeString(installment.lease?.endDate),
        checkinDate: this.sanitizeString(installment.lease?.checkinDate),
        checkoutDate: this.sanitizeString(installment.lease?.checkoutDate),
        status: this.sanitizeString(installment.lease?.status),
        refNumber: this.sanitizeString(installment.lease?.refNumber),
        leaseTypeId: this.sanitizeString(installment.lease?.leaseTypeId),
        currencyId: this.sanitizeString(installment.lease?.currencyId),
        active: Boolean(installment.lease?.active)
      },
      active: Boolean(installment.active)
    };
  }


  private static sanitizeString(value: any): string {
    if (typeof value === 'string') {
      return value.replace(/[<>]/g, '').trim();
    }
    return '';
  }

  private static sanitizeNumber(value: any): number {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }
}
